import { useEffect, useRef, type ReactNode } from 'react';
import { TitleBackdrop } from '../components/TitleBackdrop';
import { Button } from '../components/Button';
import { Screen } from '../components/Screen';
import { copy } from '../copy';
import { TITLE_SKY, release, titleLayers } from '../assets';
import { usePermissionGate } from '../flows/usePermissionGate';
import type { PermissionResult } from '../capture';

/**
 * Web step 1, on the campaign key art.
 *
 * The permission ask lives here rather than on a screen of its own — the
 * storyboard board shows camera and mic rows on the title page, and a guest who
 * has already granted both should never see a second screen asking again.
 *
 * The in-app-browser check happens before this mounts, in WebFlow: a webview
 * may be unable to grant a camera at all, so warning after the prompt has been
 * refused is too late to help.
 */
export function TitleScreen({
  onStart,
  onDenied,
}: {
  onStart: (result: PermissionResult | null) => void;
  onDenied: () => void;
}) {
  const gate = usePermissionGate();
  const ready = gate.state === 'granted';

  /**
   * Whether this guest tapped to grant, as opposed to arriving with permission
   * already in place. Tapping is intent to start, so a grant that follows one
   * goes straight on; a revisit stops here and offers a Start button, because
   * nothing has been read yet.
   */
  const askedRef = useRef(false);
  // Both hand-offs are one-shot. The parent passes inline arrows, so without
  // this the effects would re-fire on every render for as long as the state
  // held, and onStart would be called repeatedly.
  const handedOffRef = useRef(false);

  const onStartRef = useRef(onStart);
  onStartRef.current = onStart;
  const onDeniedRef = useRef(onDenied);
  onDeniedRef.current = onDenied;

  useEffect(() => {
    if (handedOffRef.current) return;
    if (gate.state === 'denied') {
      handedOffRef.current = true;
      onDeniedRef.current();
      return;
    }
    if (gate.state === 'granted' && askedRef.current) {
      handedOffRef.current = true;
      onStartRef.current(gate.result);
    }
  }, [gate.state, gate.result]);

  return (
    <Screen backdrop={<TitleBackdrop />} testId="title">
      <div className="pt-12" />

      {/*
        A scrim, not a solid: the art is the point of the screen, but cream type
        on cream needs the ground held down under the copy to stay readable.

        The headline is a question, not a title, and is set well below Title
        scale — at text-4xl uppercase it ran to five lines, filled the card and
        pushed the CTA off the bottom of a phone. The film's name is on the art
        behind it, so this does not need to shout.
      */}
      {/*
        Not ScreenBody: that centres its content, and a `justify-end` passed
        through className is the same CSS property, so which one wins depends on
        Tailwind's output order rather than on anything readable here.

        One column of copy, centred on its own axis — lockup, release line,
        headline, permission rows and CTA all share a centreline rather than
        ragging off a left edge.

        On a phone it sits at the foot of the window, under the cast's feet, on
        a scrim — the art fills the frame there and unbacked type would be
        unreadable over the figures. On a wide screen it moves to the left of
        the art, drops the scrim to sit on open sky, and centres vertically
        against the full-height cast beside it.
      */}
      <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-end pb-1 text-center md:items-start md:justify-center md:pb-0 md:pl-[6%]">
        {/* A solid fill at every width, not a frosted panel and not mobile-only.
            It carried a backdrop-blur and then partial opacity; both let the
            cast's legs through and read as a hazy layer floating over the art.
            It also used to drop to transparent at md, on the assumption the copy
            column sat on open sky there — it does not: the cast is wide enough
            to run behind the copy from 768px up to about 1180px, so the ground
            has to stay. Where it overlaps sky it is invisible anyway, being the
            same sampled colour. */}
        <div className="flex w-full max-w-sm flex-col items-center space-y-4 rounded-3xl px-5 py-5 md:max-w-md"
          // The sampled sky with an alpha byte appended — 0xEB is 0.92 — so the
          // colour itself stays single-source and only the transparency lives
          // here. Enough to let the art register behind the panel without the
          // copy losing its ground.
          style={{ backgroundColor: `${TITLE_SKY}EB` }}>
          {/* Lockup and release line are one unit, so they stay tight together
              while everything else is spaced on the parent's rhythm. */}
          <div className="w-full space-y-1">
            <img
              src={titleLayers.wordmark.fallback}
              alt="The Incomer"
              className="mx-auto w-64 select-none md:w-96"
            />
            <p className="text-xs uppercase tracking-widest text-ink/50">{release.line}</p>
          </div>

          <h1 className="text-xl font-bold leading-tight text-ink md:text-2xl">
            {copy.title.heading}
          </h1>

          <div className="w-full space-y-2">
            <PermissionRow
              label={copy.title.cameraRow}
              state={rowState(gate.state, gate.result?.cameraGranted)}
              icon={<CameraIcon />}
            />
            <PermissionRow
              label={copy.title.micRow}
              state={rowState(gate.state, gate.result?.micGranted)}
              icon={<MicIcon />}
            />
          </div>

          <Button
            testId="title-cta"
            onArt
            onClick={() => {
              if (ready) {
                onStart(gate.result);
                return;
              }
              askedRef.current = true;
              gate.ask();
            }}
            disabled={gate.state === 'checking' || gate.state === 'asking'}
          >
            {gate.state === 'asking'
              ? copy.permission.asking
              : ready
                ? copy.title.ctaReady
                : copy.title.cta}
          </Button>

          {/*
            The kiosk prints the bare host — "go to theincomercalls.com and
            enter this code" — so a guest redeeming a code lands here, not on
            /c. This is the only thing on the site that points at it.

            A plain anchor rather than a click handler: /c is a real URL the
            SPA already resolves on load (App reads the pathname), so a full
            navigation is both correct and shareable, and it costs nothing on
            a screen that has not started a session yet.

            Deliberately quiet and below the CTA. These guests already have
            their clip and are here for one thing, so it only has to be
            findable — not to compete with the activation.
          */}
          <a
            href="/c"
            className="text-xs text-ink/60 underline underline-offset-2 hover:text-ink"
          >
            {copy.title.codeLink}
          </a>
        </div>
      </div>
    </Screen>
  );
}

type RowState = 'pending' | 'granted' | 'denied';

/** Undefined means we have not asked yet, which is pending rather than refused. */
function rowState(gate: string, granted: boolean | undefined): RowState {
  if (granted === true) return 'granted';
  if (gate === 'denied' || gate === 'granted') return granted === false ? 'denied' : 'pending';
  return 'pending';
}

function PermissionRow({
  label,
  state,
  icon,
}: {
  label: string;
  state: RowState;
  icon: ReactNode;
}) {
  const note =
    state === 'granted'
      ? copy.title.rowGranted
      : state === 'denied'
        ? copy.title.rowDenied
        : copy.title.rowPending;

  // Dark pills on the cream ground, per the design. The state sits on the right
  // in a quieter weight so the row reads as a label first and a status second.
  return (
    <div className="flex items-center gap-3 rounded-xl bg-ink px-4 py-3 text-left">
      <span className="flex-none text-bone/70">{icon}</span>
      <span className="flex-1 text-sm text-bone">{label}</span>
      <span
        className={`text-[0.65rem] uppercase tracking-[0.15em] ${
          state === 'granted' ? 'text-moss' : state === 'denied' ? 'text-blood' : 'text-bone/45'
        }`}
      >
        {note}
      </span>
    </div>
  );
}

function CameraIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M23 7l-7 5 7 5V7z" />
      <rect x="1" y="5" width="15" height="14" rx="2" />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <rect x="9" y="2" width="6" height="11" rx="3" />
      <path d="M5 10v1a7 7 0 0014 0v-1M12 18v4" />
    </svg>
  );
}
